import {
  DateTimeRecord,
  DateRecord,
  Schedule,
  Rule,
  dateRecordToLocalisedDate,
  TimeRange,
  dateTimeToDateRecord,
  makeTimeRange,
  makeTimeRecord,
  timeRecordToMinutesFromMidnight,
  minutesFromMidnightToTimeRecord,
  MinutesFromMidnight,
  makeMinutesFromMidnight,
  TimeZone,
  makeDateTimeRecord,
  dateTimeRecordToLocalisedDateTime,
  compareDateTimeRecords,
} from "../types";
import { makeRuleFunction } from "./rule_function";
import { EvaluationError } from "./errors";
import { last } from "es-toolkit";

export function getRulesInPriorityOrder<TFeatureName extends string = string, TFeatureState extends string = string>(
  schedule: Schedule<TFeatureName, TFeatureState>,
) {
  // We're working with the assumption that newer rules will be appended and they are
  // more likely to be exceptions which should take priority over more general rules.
  return [...schedule.rules].reverse();
}

export function evaluateStateAt<TFeatureName extends string = string, TFeatureState extends string = string>(
  schedule: Schedule<TFeatureName, TFeatureState>,
  feature: NoInfer<TFeatureName>,
  dt: DateTimeRecord,
): TFeatureState {
  return evaluateAt(schedule, [feature], dt)[0].state;
}

export interface FeatureEvaluation<TFeatureName extends string = string, TFeatureState extends string = string> {
  feature: TFeatureName;
  state: TFeatureState;
  rule: Rule<TFeatureName, TFeatureState> | null;
}

/**
 * Evaluate multiple features in a single pass over the schedule's rules.
 *
 * For each feature, returns the winning state and the rule that determined it
 * (`null` when the default state applies). Calling `appliesAt` once per rule
 * rather than once per rule per feature.
 *
 * The return tuple length matches the input `features` length — passing a
 * single-element tuple returns a single-element tuple.
 */
export function evaluateAt<
  TFeatureName extends string = string,
  TFeatureState extends string = string,
  const F extends readonly NoInfer<TFeatureName>[] = readonly NoInfer<TFeatureName>[],
>(
  schedule: Schedule<TFeatureName, TFeatureState>,
  features: F,
  dt: DateTimeRecord,
): { [K in keyof F]: FeatureEvaluation<F[K], TFeatureState> };
export function evaluateAt<TFeatureName extends string, TFeatureState extends string>(
  schedule: Schedule<TFeatureName, TFeatureState>,
  features: readonly TFeatureName[],
  dt: DateTimeRecord,
): FeatureEvaluation<TFeatureName, TFeatureState>[] {
  for (const f of features) {
    if (!Object.hasOwn(schedule.features, f)) {
      throw new EvaluationError(`Cannot evaluate state: feature ${f} does not exist on schedule`);
    }
  }

  const results = new Map<TFeatureName, { state: TFeatureState; rule: Rule<TFeatureName, TFeatureState> | null }>();
  const pending = new Set(features);

  const rules = [...schedule.rules].reverse();
  for (const rule of rules) {
    if (pending.size === 0) break;

    const affected = [...pending].filter((f) => rule.features[f] !== undefined);
    if (affected.length === 0) continue;

    const func = makeRuleFunction(schedule, rule);
    if (!func.appliesAt(dt)) continue;

    for (const f of affected) {
      const state = rule.features[f];
      if (state !== undefined) results.set(f, { state, rule });
      pending.delete(f);
    }
  }

  for (const f of pending) {
    results.set(f, { state: schedule.features[f].default_state, rule: null });
  }

  return features.map((f) => {
    const r = results.get(f);
    if (r === undefined) throw new EvaluationError(`Cannot evaluate state: feature ${f} was not resolved`);
    return { feature: f, state: r.state, rule: r.rule };
  });
}

interface DailyStatePeriods<TFeatureState extends string = string> {
  state: TFeatureState;
  periods: TimeRange[];
}

interface DailyStateSegment<TFeatureState extends string = string> {
  period: TimeRange;
  state: TFeatureState;
}

interface DailyStateSegments<TFeatureName extends string = string, TFeatureState extends string = string> {
  feature: TFeatureName;
  timezone: TimeZone;
  date: DateRecord;
  segments: DailyStateSegment<TFeatureState>[];
}

/**
 * Evaluate daily state segments for one or more features over a date range.
 *
 * When multiple features are provided, `makeRuleFunction` and `periodsAppliedOn`
 * are called once per rule per day rather than once per rule per feature per day.
 * Yields one {@link DailyStateSegments} per feature per day.
 */
export function* evaluateDailyStateSegments<
  TFeatureName extends string = string,
  TFeatureState extends string = string,
>(
  schedule: Schedule<TFeatureName, TFeatureState>,
  featureOrFeatures: NoInfer<TFeatureName> | readonly NoInfer<TFeatureName>[],
  boundaries: {
    start: DateRecord;
    end: DateRecord;
  },
): Generator<DailyStateSegments<TFeatureName, TFeatureState>> {
  const features = (
    Array.isArray(featureOrFeatures) ? featureOrFeatures : [featureOrFeatures]) as readonly TFeatureName[];

  const scheduleFeatures = schedule.features as Record<string, { default_state: string; allowed_states: string[] }>;

  const defaultStates = new Map<string, TFeatureState>();
  for (const feat of features) {
    const def = scheduleFeatures[feat];
    if (!Object.hasOwn(scheduleFeatures, feat) || def === undefined) {
      throw new EvaluationError(`Cannot evaluate state segments: feature ${feat} does not exist on schedule`);
    }
    defaultStates.set(feat, def.default_state as TFeatureState);
  }

  const startDate = dateRecordToLocalisedDate(boundaries.start, schedule.metadata.timezone);
  const endDate = dateRecordToLocalisedDate(boundaries.end, schedule.metadata.timezone);
  if (startDate.hasSame(endDate, "day") || startDate > endDate) {
    throw new EvaluationError(
      `Cannot evaluate state segments: start must be earlier than end - ${JSON.stringify(boundaries)}`,
    );
  }

  const featureSet = new Set<string>(features);
  const rules = [...schedule.rules]
    .reverse()
    .filter((rule) => Object.keys(rule.features).some((f) => featureSet.has(f)))
    .map((rule) => ({
      featureStates: rule.features as Record<string, string>,
      func: makeRuleFunction(schedule, rule as Rule<TFeatureName, TFeatureState>),
    }));

  let currentDate = startDate;
  while (!currentDate.hasSame(endDate, "day")) {
    const date = dateTimeToDateRecord(currentDate);
    const rulePeriods = rules.map(({ featureStates, func }) => ({
      featureStates,
      periods: func.periodsAppliedOn(date).periods,
    }));

    for (const feat of features) {
      const defaultState = defaultStates.get(feat as string) as TFeatureState;
      const featureRulePeriods: DailyStatePeriods<TFeatureState>[] = rulePeriods
        .filter(({ featureStates }) => (feat as string) in featureStates)
        .map(({ featureStates, periods }) => ({
          state: featureStates[feat as string] as TFeatureState,
          periods,
        }));
      const defaultPeriod: DailyStatePeriods<TFeatureState> = {
        state: defaultState,
        periods: [makeTimeRange(makeTimeRecord(0, 0), makeTimeRecord(24, 0))],
      };
      const segments = segmentStatePeriods([...featureRulePeriods, defaultPeriod]);
      yield {
        timezone: schedule.metadata.timezone,
        feature: feat,
        date,
        segments,
      };
    }
    currentDate = currentDate.plus({ days: 1 });
  }
}

/**
 * Represents a contiguous time range with a specific state.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export interface StateRange<TFeatureName extends string = string, TFeatureState extends string = string> {
  feature: TFeatureName;
  state: TFeatureState;
  timezone: TimeZone;
  start: DateTimeRecord;
  end: DateTimeRecord;
}

/**
 * Evaluate state ranges for one or more features over a time period.
 *
 * When multiple features are provided, rule evaluation is shared across features
 * (see {@link evaluateDailyStateSegments}). Yields {@link StateRange} objects
 * for each feature; ranges from different features may be interleaved.
 */
export function* evaluateStateRanges<TFeatureName extends string = string, TFeatureState extends string = string>(
  schedule: Schedule<TFeatureName, TFeatureState>,
  featureOrFeatures: NoInfer<TFeatureName> | readonly NoInfer<TFeatureName>[],
  boundaries: {
    start: DateTimeRecord;
    end: DateTimeRecord;
  },
): Generator<StateRange<TFeatureName, TFeatureState>> {
  const features = (
    Array.isArray(featureOrFeatures) ? featureOrFeatures : [featureOrFeatures]) as readonly TFeatureName[];

  const timezone = schedule.metadata.timezone;
  const boundaryStartDT = dateTimeRecordToLocalisedDateTime(boundaries.start, timezone);
  const boundaryEndDT = dateTimeRecordToLocalisedDateTime(boundaries.end, timezone);
  const dateBoundaries = {
    start: dateTimeToDateRecord(boundaryStartDT),
    end: dateTimeToDateRecord(boundaryEndDT.set({ hour: 0, minute: 0 }).plus({ days: 1 })),
  };

  const currentRanges = new Map<TFeatureName, StateRange<TFeatureName, TFeatureState>>();

  for (const dailySegments of evaluateDailyStateSegments(schedule, features, dateBoundaries)) {
    const feature = dailySegments.feature;
    for (const segment of dailySegments.segments) {
      const segmentStart = makeDateTimeRecord(
        dailySegments.date.year,
        dailySegments.date.month,
        dailySegments.date.day,
        segment.period.start.hour,
        segment.period.start.minute,
      );
      const segmentStartDT = dateTimeRecordToLocalisedDateTime(segmentStart, timezone);
      const segmentEnd = makeDateTimeRecord(
        dailySegments.date.year,
        dailySegments.date.month,
        dailySegments.date.day,
        segment.period.end.hour,
        segment.period.end.minute,
      );
      const segmentEndDT = dateTimeRecordToLocalisedDateTime(segmentEnd, timezone);
      if (segmentEndDT < boundaryStartDT) {
        continue;
      }
      if (segmentStartDT >= boundaryEndDT) {
        break;
      }
      const trimmedStart = segmentStartDT < boundaryStartDT ? boundaries.start : segmentStart;
      const trimmedEnd = segmentEndDT > boundaryEndDT ? boundaries.end : segmentEnd;
      const currentRange = currentRanges.get(feature) ?? null;
      if (currentRange === null) {
        currentRanges.set(feature, {
          feature,
          state: segment.state,
          timezone: dailySegments.timezone,
          start: trimmedStart,
          end: trimmedEnd,
        });
      } else if (currentRange.state === segment.state) {
        currentRange.end = trimmedEnd;
      } else {
        yield currentRange;
        currentRanges.set(feature, {
          feature,
          state: segment.state,
          timezone: dailySegments.timezone,
          start: trimmedStart,
          end: trimmedEnd,
        });
      }
    }
  }
  for (const range of currentRanges.values()) {
    yield range;
  }
}

/**
 * Filter state ranges to only include specified states.
 *
 * @template TFeatureName - The type of feature names
 * @template TFeatureState - The type of feature states
 */
export function* filterStateRanges<TFeatureName extends string = string, TFeatureState extends string = string>(
  stateRanges: Generator<StateRange<TFeatureName, TFeatureState>>,
  states: TFeatureState[],
): Generator<StateRange<TFeatureName, TFeatureState>> {
  const stateLookup = new Set(states);
  for (const stateRange of stateRanges) {
    if (stateLookup.has(stateRange.state)) {
      yield stateRange;
    }
  }
}

/**
 * Represents a time range without feature/state info (for merged results).
 */
export interface TimeRangeRecord {
  timezone: TimeZone;
  start: DateTimeRecord;
  end: DateTimeRecord;
}

/**
 * Merge overlapping or adjacent time ranges into continuous periods.
 */
function mergeOverlappingRanges(ranges: Pick<StateRange, "start" | "end">[], timezone: TimeZone): TimeRangeRecord[] {
  const sorted = [...ranges].sort((a, b) => compareDateTimeRecords(a.start, b.start));

  return sorted.reduce<TimeRangeRecord[]>((merged, range) => {
    const current = merged.at(-1);
    if (current && compareDateTimeRecords(range.start, current.end) <= 0) {
      // Overlapping or adjacent: extend if this range goes further
      if (compareDateTimeRecords(range.end, current.end) > 0) {
        merged[merged.length - 1] = { ...current, end: range.end };
      }
    } else {
      merged.push({ timezone, start: range.start, end: range.end });
    }
    return merged;
  }, []);
}

/**
 * A feature/state requirement for matching.
 */
export interface FeatureStateRequirement<TFeatureName extends string = string, TFeatureState extends string = string> {
  feature: TFeatureName;
  state: TFeatureState;
}

/**
 * Evaluate merged time ranges where ANY of the requirements is satisfied.
 * Returns the union of all matching ranges, merged to remove overlaps.
 */
export function evaluateMatchingRanges<TFeatureName extends string = string, TFeatureState extends string = string>(
  schedule: Schedule<TFeatureName, TFeatureState>,
  requirements: FeatureStateRequirement<NoInfer<TFeatureName>, NoInfer<TFeatureState>>[],
  boundaries: { start: DateTimeRecord; end: DateTimeRecord },
): TimeRangeRecord[] {
  const allRanges: StateRange<TFeatureName, TFeatureState>[] = [];

  for (const req of requirements) {
    const ranges = filterStateRanges(evaluateStateRanges(schedule, req.feature, boundaries), [req.state]);
    allRanges.push(...ranges);
  }

  return mergeOverlappingRanges(allRanges, schedule.metadata.timezone);
}

/**
 * Flatten multiple overlapping and intersecting stateful periods of time into a
 * single sequence representing stateful segments of a time period.
 *
 * Rough example:
 *   periods like
 *    - armed: 00:00-06:00, 18:00-24:00
 *    - disarmed: 00:00-24:00
 *   would result in state segments
 *    - {state: armed, 00:00-06:00}
 *    - {state: disarmed, 06:00-18:00}
 *    - {state: armed, 18:00-24:00}
 *
 * Determination of state for any segment is determined based on priority, which is
 * resolved based on ascending order in the sequence of periods. Eg: first period
 * wins, then second period, etc.
 *
 * @template TFeatureState - The type of feature states
 */
export function segmentStatePeriods<TFeatureState extends string = string>(
  statePeriods: DailyStatePeriods<TFeatureState>[],
): DailyStateSegment<TFeatureState>[] {
  // We implement the sweep line algorithm to flatten all of the periods into
  // a single sequence. However, given the periods may be contributed by multiple
  // rules or defaults, our implementation needs to infer a priority to resolve
  // ambiguous situations, eg: overlapping or intersecting periods.
  interface Event {
    time: MinutesFromMidnight;
    type: "start" | "end";
    state: TFeatureState;
    priority: number;
  }
  const events: Event[] = [];

  for (const [i, statePeriod] of statePeriods.entries()) {
    const priority = statePeriods.length - i;
    for (const period of statePeriod.periods) {
      const startTime = timeRecordToMinutesFromMidnight(period.start);
      const endTime = timeRecordToMinutesFromMidnight(period.end);
      if (startTime !== endTime) {
        events.push({
          time: startTime,
          type: "start",
          state: statePeriod.state,
          priority,
        });
        events.push({
          time: endTime,
          type: "end",
          state: statePeriod.state,
          priority,
        });
      }
    }
  }

  // Sort events by time, then type (end before start), then priority
  events.sort((a, b) => {
    if (a.time !== b.time) {
      return a.time - b.time;
    }
    if (a.type !== b.type) {
      return a.type === "end" ? -1 : 1;
    }
    return a.priority - b.priority;
  });

  const segments: DailyStateSegment<TFeatureState>[] = [];
  // Multiple intersecting periods could lead to multiple states with multiple intersecting
  // priorities. We need to track each of them as we sweep through
  const activeStatePriorities = new Map<TFeatureState, Set<number>>();

  const getHighestPriorityState = (): TFeatureState | null => {
    let highestPriority = -1;
    let highestPriorityState: TFeatureState | null = null;
    for (const [state, priorities] of activeStatePriorities) {
      const activePriority = Math.max(...priorities);
      if (activePriority > highestPriority) {
        highestPriority = activePriority;
        highestPriorityState = state;
      }
    }
    return highestPriorityState;
  };

  let currentState: TFeatureState | null = null;
  let segmentStart = makeMinutesFromMidnight(0);
  for (const event of events) {
    if (event.type === "start") {
      let priorities = activeStatePriorities.get(event.state);
      if (!priorities) {
        priorities = new Set();
        activeStatePriorities.set(event.state, priorities);
      }
      priorities.add(event.priority);
    } else {
      const priorities = activeStatePriorities.get(event.state);
      if (priorities) {
        priorities.delete(event.priority);
        if (priorities.size === 0) {
          activeStatePriorities.delete(event.state);
        }
      }
    }

    const newState = getHighestPriorityState();
    if (currentState !== newState) {
      // Close current segment if it exists
      if (currentState !== null && segmentStart < event.time) {
        segments.push({
          period: makeTimeRange(
            minutesFromMidnightToTimeRecord(segmentStart),
            minutesFromMidnightToTimeRecord(event.time),
          ),
          state: currentState,
        });
      }
      currentState = newState;
      segmentStart = event.time;
    }
  }

  // Merge adjacent segments with the same state.
  // Example: "armed 10:00-11:00" and "armed 11:00-12:00" should be merged
  // into "armed 10:00-12:00"
  const mergedSegments: DailyStateSegment<TFeatureState>[] = [];
  for (const segment of segments) {
    const lastAddedSegment = last(mergedSegments);
    if (
      lastAddedSegment &&
      lastAddedSegment.state === segment.state &&
      timeRecordToMinutesFromMidnight(lastAddedSegment.period.end) ===
        timeRecordToMinutesFromMidnight(segment.period.start)
    ) {
      lastAddedSegment.period = makeTimeRange(lastAddedSegment.period.start, segment.period.end);
    } else {
      mergedSegments.push(segment);
    }
  }
  return mergedSegments;
}
